/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

import { PlaybackSession } from '../../session/PlaybackSession';
import { createSafeTimeout, Logger, TimeoutController, Error } from '@quickplay/foundation';
import {
  Action,
  Ad,
  AdBreak,
  AdBreakInfo,
  AdComposedPlayer,
  AdInfo,
  AdTrackingEvent,
  BUFFERING_STATES,
  DrmScheme,
  PLAYBACK_STATES,
  Player,
  PlayerContext,
  PlaybackStatistics,
  SEEKING_STATES,
  StreamTimelineEvent,
  TrackType,
  NetworkRequestObservation,
  AdContentTimePosition,
} from '@quickplay/player-interface';
import { YospaceAdsSession } from '@quickplay/ads-yospace';
import { v4 as uuid } from 'uuid';
import { Content } from '../../model/Content';
import { ApplicationContainer } from '../../model/ApplicationContainer';
import { PlaybackError } from '../../model/PlaybackError';
import { PlaybackEndReason } from '../../model/PlaybackEndReason';
import { PlaybackStartupObservation } from '../../model/PlaybackStartupObservation';
import { MediaFlow, MediaFlowKind } from '../../model/MediaFlow';
import { MediaFlowImpl } from './MediaFlowImpl';
import { isPlaybackStartupObservation } from '../util/PlaybackStartupObservationUtil';
import { CustomEvent } from '../../model/CustomEvent';
import { Provider } from '../Provider';
import { AnalyticsSessionState } from '../AnalyticsSessionState';
import { PlaybackEvent } from '../../event/PlaybackEvent';
import {
  toApplicationContainerReportingData,
  toPlaybackErrorReportingData,
  toConvivaReportingData,
  toDatazoomReportingData,
  toNPAWReportingData,
  toNewRelicContentData,
  toErrorMessage,
  mapKeySystemToDrmScheme,
  toApplicationErrorReportingData,
} from '../util/DataUtil';
import { DatazoomAnalyticsReporter } from '../reporter/model/DatazoomAnalyticsReporter';
import { ConvivaAnalyticsReporter } from '../reporter/model/ConvivaAnalyticsReporter';
import { PlaybackDataKey } from '../../datakey/PlaybackDataKey';
import { AnalyticsReporter } from '../reporter/model/AnalyticsReporter';
import { reportGodavariObservation } from '../reporter/model/GodavariPayload';
import { GodavariPlayerObserver, GodavariProgressSnapshot } from './godavari/GodavariPlayerObserver';
import { toGodavariPlayerMetadata, toGodavariProgressMetadata } from './godavari/GodavariPlayerMetadata';
import { toGodavariContentId } from '../util/GodavariMetadataUtil';
import { AdPlaybackEvent } from '../../event/AdPlaybackEvent';
import { AdDataKey } from '../../datakey/AdDataKey';
import { Constants as ConvivaConstants } from '@convivainc/conviva-js-coresdk';
import { isLive } from '../util/AnalyticsReporterUtil';
import { NPAWAnalyticsReporter } from '../reporter/model/NPAWAnalyticsReporter';
import { PLAYER_NAME } from '../../Constants';
import { ApplicationEvent } from '../../event/ApplicationEvent';
import { UserJourneyDataKey } from '../../datakey/UserJourneyDataKey';
import { ApplicationErrorDataKey } from '../../datakey/ApplicationErrorDataKey';
import { ErrorSeverity } from '../ErrorSeverity';
import { StreamType } from '../../fl-analytics';
import { PlaybackSourceType } from '../../type/PlaybackSourceType';
import { version } from '@quickplay/player';

declare global {
  interface Window {
    shaka: any;
  }
}

export class PlaybackSessionImpl implements PlaybackSession {
  private readonly DEFAULT_PROGRESS_UPDATE_INTERVAL_MS = 60_000;
  private player?: Player;
  private playerContext?: any;
  private state = AnalyticsSessionState.UNKNOWN;
  private bufferingState: BUFFERING_STATES = BUFFERING_STATES.INACTIVE;
  private isAdBreakStartReported?: boolean;
  private isAdBreakEndReported?: boolean;
  private readonly timeoutController: TimeoutController;
  private timeoutStarted = false;
  private progressIntervalRefs?: { [key: string]: any };
  private readonly restrictedProviders: Provider[];
  private contentStarted = false;
  private playbackLoadStartTime: number;
  private playbackSessionStartTimeMs = 0;
  private readonly godavariObserver = new GodavariPlayerObserver();
  private lastKnownResolution?: string;
  private playbackStartedTimeSec?: number;
  private playbackSessionId?: string;
  private programPlaybackSessionId?: string;
  private activeAd?: AdInfo | Ad;
  /** Most recently loaded advert; IMA can fire the impression before the start event. */
  private loadedAd?: AdInfo | Ad;
  private activeAdBreak?: AdBreakInfo | AdBreak;

  constructor(
    private readonly reporters: { [key: string]: AnalyticsReporter },
    private readonly content: Content,
    private readonly liveThresholdMs: number,
    private readonly vodThresholdMs: number,
    private readonly playbackSource?: PlaybackSourceType,
    private readonly applicationContainer?: ApplicationContainer,
    private readonly loggers?: { [key: string]: Logger },
    private readonly progressUpdateIntervalsInMs?: { [key: string]: number | undefined },
  ) {
    this.timeoutController = createSafeTimeout();
    this.playbackLoadStartTime = 0;
    this.restrictedProviders = [Provider.CONVIVA, Provider.NEWRELIC];

    if (!this.loggers) {
      return;
    }

    Object.values(this.loggers).forEach(logger => {
      logger.log('[ANALYTICS] playback session created');
    });
  }

  getVideoSessionId(): string | undefined {
    return this.playbackSessionId?.replace(/-/g, '');
  }

  start(): void {
    this.state = AnalyticsSessionState.STARTED;
    this.playbackSessionId = uuid();
    this.playbackSessionStartTimeMs = Date.now();
    this.godavariObserver.reset(Number(this.player?.playbackStatistics?.playTime ?? 0));
    if (this.content.playbackMode === StreamType.LIVE || this.content.isLive === StreamType.LIVE) {
      this.programPlaybackSessionId = uuid();
    }

    Object.values(this.reporters).forEach((reporter: AnalyticsReporter) => {
      switch (reporter.provider) {
        case Provider.DATAZOOM: {
          // Report metadata first, then playback request event
          this.reportMetadata(this.content, this.playbackSource, this.applicationContainer);
          reporter.reportEvent(PlaybackEvent.PLAYBACK_REQUEST, undefined, this.playerContext);
          break;
        }

        case Provider.GODAVARI: {
          reporter.reportMetadata(this.buildContentMetadata(reporter, this.content));
          reporter.reportEvent(PlaybackEvent.PLAYBACK_REQUEST);
          this.reportGodavariPlayerState(reporter);
          break;
        }

        case Provider.CONVIVA: {
          // Report playback request with Conviva-formatted content data
          reporter.reportEvent(PlaybackEvent.PLAYBACK_REQUEST, toConvivaReportingData(this.content));
          break;
        }

        case Provider.NPAW: {
          // Report playback request event
          reporter.reportEvent(PlaybackEvent.PLAYBACK_REQUEST, toNPAWReportingData(this.content));

          // Set initial content metadata as video options
          reporter.reportMetadata(toNPAWReportingData(this.content));
          break;
        }

        default: {
          this.loggers?.[reporter.provider]?.log(`[ANALYTICS] Unknown provider in start: ${reporter.provider}`);
          break;
        }
      }
      if (reporter.provider === Provider.NEWRELIC) {
        this.playbackLoadStartTime = Date.now();
        reporter.reportEvent(PlaybackEvent.PLAYBACK_START, {
          ...this.buildNewRelicContentMetadata(this.content),
          [PlaybackDataKey.SOURCE]: this.playbackSource,
        });
      }
    });
  }

  startMediaFlow(kind: MediaFlowKind): MediaFlow {
    return new MediaFlowImpl(kind, observation => this.reportStartupObservation(observation));
  }

  reportMetadata(
    content: Content,
    playbackSource?: PlaybackSourceType,
    applicationContainer?: ApplicationContainer,
  ): void {
    let metadata = {};

    if (applicationContainer) {
      metadata = {
        ...metadata,
        ...toApplicationContainerReportingData(this.applicationContainer),
      };
    }

    if (playbackSource) {
      metadata = {
        ...metadata,
        [PlaybackDataKey.PLAYBACK_SOURCE]: this.playbackSource,
      };
    }

    Object.values(this.reporters).forEach((reporter: AnalyticsReporter) => {
      metadata = { ...metadata, ...this.buildContentMetadata(reporter, content) };
      reporter.reportMetadata(metadata, this.playerContext);
    });
  }

  interruptStart(customEvent: CustomEvent): void {
    if (this.state === AnalyticsSessionState.STARTED) {
      this.state = AnalyticsSessionState.INTERRUPTED;
      this.reportEvent(customEvent.event, customEvent.data, this.playerContext);
    }
  }

  interruptEnd(customEvent: CustomEvent): void {
    if (this.state === AnalyticsSessionState.INTERRUPTED) {
      this.state = AnalyticsSessionState.STARTED;
      this.reportEvent(customEvent.event, customEvent.data, this.playerContext);
    }
  }

  addEvent(customEvent: CustomEvent): void {
    Object.values(this.reporters).forEach((reporter: AnalyticsReporter) => {
      switch (reporter.provider) {
        case Provider.NEWRELIC:
          reporter.reportEvent(customEvent.event, {
            ...this.buildNewRelicContentMetadata(this.content),
            ...customEvent.data,
          });
          break;

        case Provider.GODAVARI:
          reporter.reportEvent(customEvent.event, customEvent.data);
          break;

        case Provider.DATAZOOM:
          reporter.reportEvent(customEvent.event, customEvent.data, this.playerContext);
          break;

        default:
          //no-op
          break;
      }
    });
  }

  reportStartupObservation(observation: PlaybackStartupObservation): void {
    if (this.state !== AnalyticsSessionState.STARTED) return;
    if (!isPlaybackStartupObservation(observation)) {
      Object.values(this.loggers || {}).forEach(logger =>
        logger?.warn('Ignoring invalid playback startup observation.'),
      );
      return;
    }
    Object.values(this.reporters).forEach(reporter => reporter.reportStartupObservation?.(observation));
  }

  attachPlayer(player: Player): void {
    this.player = player;
    this.subscribeToPlayerEvents(this.player);
    Object.values(this.reporters).forEach((reporter: AnalyticsReporter) => {
      switch (reporter.provider) {
        case Provider.DATAZOOM: {
          if (player.internalPlayerName !== 'shaka-player') {
            this.loggers?.[reporter.provider].log('[ANALYTICS] attached player is not a Shaka player');
            return;
          }

          // Shaka library needs to be manually assigned to the window.shaka property, so Datazoom can
          // create a context and start reporting playback events, otherwise the Datazoom plugin will
          // throw the following error in console:
          // error TypeError: Cannot read properties of undefined (reading 'Player')
          window.shaka = player.rawLibrary;

          const datazoomReporter = reporter as DatazoomAnalyticsReporter;
          this.playerContext = datazoomReporter.createContext(player);
          this.playerContext.startContentSession();
          break;
        }

        case Provider.GODAVARI:
          reporter.reportMetadata(this.buildContentMetadata(reporter, this.content));
          if (this.state === AnalyticsSessionState.STARTED) {
            this.reportGodavariPlayerState(reporter);
          }
          break;

        case Provider.CONVIVA: {
          const convivaReporter = reporter as ConvivaAnalyticsReporter;
          convivaReporter.attachPlayer(player);
          break;
        }

        case Provider.NPAW: {
          const npawReporter = reporter as NPAWAnalyticsReporter;
          npawReporter.attachPlayer(player);

          // Set video options immediately after attachment
          const videoMetadata = this.buildNPAWVideoMetadata();
          reporter.reportMetadata(videoMetadata);
          break;
        }
      }
    });
  }

  attachYospaceSession(yospaceSession: YospaceAdsSession): void {
    if (yospaceSession && this.playerContext?.attachYospaceSession) {
      this.playerContext.attachYospaceSession(yospaceSession.session);
    }
  }

  stop(error?: PlaybackError, endReason?: PlaybackEndReason): void {
    if (this.state === AnalyticsSessionState.STARTED) {
      const reason = endReason ?? (error ? undefined : 'user-stop');
      if (reason) {
        Object.values(this.reporters).forEach(reporter => reporter.reportPlaybackEndIntent?.(reason));
      }
      if (error) {
        Object.values(this.reporters).forEach((reporter: AnalyticsReporter) => {
          const metadata = this.buildContentMetadata(reporter, error.content);
          if (reporter.provider === Provider.NEWRELIC) {
            const playTime = this.player?.playbackStatistics?.playTime;
            const currentPosition = playTime != null && playTime > 0 ? playTime : (this.player?.currentTime ?? 0);
            reporter.reportEvent(ApplicationEvent.ERROR, {
              ...this.buildNewRelicContentMetadata(this.content),
              ...toApplicationErrorReportingData(
                { ...error, errorType: 'PLAYBACK_ERROR', severity: ErrorSeverity.CRITICAL },
                reporter.provider,
              ),
              [PlaybackDataKey.CURRENT_POSITION]: Math.ceil(currentPosition),
            });
          } else {
            reporter.reportEvent(
              PlaybackEvent.PLAYBACK_ERROR,
              toPlaybackErrorReportingData(error, metadata),
              this.playerContext,
            );
          }
        });
      }

      Object.values(this.reporters).forEach(reporter => {
        if (reporter.provider === Provider.GODAVARI) {
          if (this.isMainContext(this.player)) {
            reporter.reportMetadata(this.buildContentMetadata(reporter, this.content));
          }
          reporter.reportEvent(PlaybackEvent.PLAYBACK_END);
        }
      });

      this.state = AnalyticsSessionState.STOPPED;
      this.playbackSessionId = undefined;
      this.programPlaybackSessionId = undefined;

      this.unsubscribeFromPlayerEvents(this.player);

      // playerContext might not exist when trying to stop a PlaybackSession even before the player is created.
      // e.g, use case - auth failure
      if (this.playerContext) {
        this.playerContext.destroy();
        this.playerContext = null;
      }
    }
  }

  setMetadata(metadata: { [key: string]: any }): void {
    Object.values(this.reporters).forEach((reporter: AnalyticsReporter) => {
      if (reporter.provider === Provider.NEWRELIC) {
        this.content.customMetadata = { ...this.content.customMetadata, ...metadata };
        return;
      }
      reporter.reportMetadata(metadata, this.playerContext);
    });
  }

  /**
   * Subscribes this playback session to the provided player's events.
   * @param player
   * @private
   */
  private subscribeToPlayerEvents(player: Player): void {
    player.subscribe('audiotrackchanged', this.onAudioTrackChanged);
    player.subscribe('textchanged', this.onTextTracksChanged);
    player.subscribe('playbackstatechanged', this.onPlaybackStateChanged);
    player.subscribe('seekingstatechanged', this.onSeekingStateChanged);
    player.subscribe('bufferingstatechanged', this.onBufferingStateChanged);
    player.subscribe('adrequeststart', this.onAdRequestStart);
    player.subscribe('adrequestend', this.onAdRequestEnd);
    player.subscribe('adbreakstart', this.onAdBreakStart);
    player.subscribe('adbreakend', this.onAdBreakEnd);
    player.subscribe('adloaded', this.onAdLoaded);
    player.subscribe('adstart', this.onAdStart);
    player.subscribe('adend', this.onAdEnd);
    player.subscribe('adskip', this.onAdSkip);
    player.subscribe('adskip_afc_policy', this.onAdSkipAfcPolicy);
    player.subscribe('adprogressupdate', this.onAdProgressUpdate);
    player.subscribe('adverttrackingevent', this.onAdTracking);
    player.subscribe('bandwidthchanged', this.onBandwidthChanged);
    player.subscribe('error', this.onError);
    player.subscribe('aderror', this.onAdError);
    player.subscribe('streamended', this.onStreamEnded);
    player.subscribe('streamtimelineevent', this.onStreamTimelineEvent);
    player.subscribe('engagementscore', this.onEngagementScore);

    // Subscribe to progressupdate only if NewRelic reporter exists
    const hasNewRelic =
      this.reporters && Object.values(this.reporters).some(reporter => reporter.provider === Provider.NEWRELIC);

    if (hasNewRelic) {
      player.subscribe('progressupdate', this.onProgressForPlaybackStart);
    }

    if (Object.values(this.reporters).some(reporter => reporter.provider === Provider.GODAVARI)) {
      player.subscribe('progressupdate', this.onProgressForGodavari);
      player.subscribe('manifestparsed', this.onManifestParsedForGodavari);
      player.subscribe('networkrequest', this.onNetworkRequestForGodavari);
      player.subscribe('networkresponse', this.onNetworkResponseForGodavari);
    }
  }

  /**
   * Unsubscribes this playback session from the provided player's events.
   * @param player
   * @private
   */
  private unsubscribeFromPlayerEvents(player?: Player): void {
    if (!player) {
      return;
    }

    if (this.loggers) {
      Object.values(this.loggers).forEach((logger: Logger) => {
        logger.log('[ANALYTICS][PLAYBACK_SESSION] unsubscribing from player events');
      });
    }

    player.unsubscribe('audiotrackchanged', this.onAudioTrackChanged);
    player.unsubscribe('textchanged', this.onTextTracksChanged);
    player.unsubscribe('playbackstatechanged', this.onPlaybackStateChanged);
    player.unsubscribe('seekingstatechanged', this.onSeekingStateChanged);
    player.unsubscribe('bufferingstatechanged', this.onBufferingStateChanged);
    player.unsubscribe('adrequeststart', this.onAdRequestStart);
    player.unsubscribe('adrequestend', this.onAdRequestEnd);
    player.unsubscribe('adbreakstart', this.onAdBreakStart);
    player.unsubscribe('adbreakend', this.onAdBreakEnd);
    player.unsubscribe('adloaded', this.onAdLoaded);
    player.unsubscribe('adstart', this.onAdStart);
    player.unsubscribe('adend', this.onAdEnd);
    player.unsubscribe('adskip', this.onAdSkip);
    player.unsubscribe('adskip_afc_policy', this.onAdSkipAfcPolicy);
    player.unsubscribe('adprogressupdate', this.onAdProgressUpdate);
    player.unsubscribe('adverttrackingevent', this.onAdTracking);
    player.unsubscribe('bandwidthchanged', this.onBandwidthChanged);
    // dev note: passing the reference to this.onError here causing an exception
    // which prevents other subscribed callbacks from firing
    player.unsubscribe('error');
    player.unsubscribe('aderror', this.onAdError);
    player.unsubscribe('streamended', this.onStreamEnded);
    player.unsubscribe('streamtimelineevent', this.onStreamTimelineEvent);
    player.unsubscribe('engagementscore', this.onEngagementScore);
    if (this.reporters[Provider.GODAVARI]) {
      player.unsubscribe('progressupdate', this.onProgressForGodavari);
      player.unsubscribe('manifestparsed', this.onManifestParsedForGodavari);
      player.unsubscribe('networkrequest', this.onNetworkRequestForGodavari);
      player.unsubscribe('networkresponse', this.onNetworkResponseForGodavari);
    }
  }

  private readonly onEngagementScore = (score: string): void => {
    Object.values(this.reporters).forEach((reporter: AnalyticsReporter) => {
      if (reporter.provider === Provider.NEWRELIC) {
        reporter.reportEvent(PlaybackEvent.PLAYBACK_SUMMARY, {
          ...this.buildNewRelicContentMetadata(this.content),
          [PlaybackDataKey.ENGAGEMENT_SCORE]: score,
        });
      }
    });
  };

  private readonly onProgressForPlaybackStart = (): void => {
    const newRelicReporter = Object.values(this.reporters).find(reporter => reporter.provider === Provider.NEWRELIC);

    if (!newRelicReporter) {
      return;
    }
    this.handleNewRelicPlaybackStarted(newRelicReporter);
    this.contentStarted = true;

    this.player?.unsubscribe('progressupdate', this.onProgressForPlaybackStart);
  };

  private readonly onProgressForGodavari = (): void => {
    const reporter = this.reporters[Provider.GODAVARI];
    if (reporter && this.isMainContext(this.player) && !this.isAdBreakStartReported) {
      const observation = { statistics: this.player?.playbackStatistics };
      if (this.godavariObserver.hasStarted) {
        this.updateGodavariWatchTime(observation.statistics);
        this.godavariObserver.captureStartingBitrate(observation.statistics?.variantBitrate);
        reporter.reportMetadata(
          toGodavariProgressMetadata({
            content: this.content,
            player: {
              currentTime: this.player?.currentTime,
              playbackRate: this.player?.playbackRate,
              duration: this.player?.duration,
              isLive: this.player?.isLive,
            },
            statistics: observation.statistics,
            startingBitrate: this.godavariObserver.startingBitrate,
            normalizedWatchTime: this.godavariObserver.normalizedWatchTime,
          }),
        );
      } else {
        // Refresh startup data until the first advancing observation confirms content play.
        this.reportGodavariPlayerMetadata(reporter, observation);
      }
      if (
        this.godavariObserver.hasStarted &&
        typeof observation.statistics?.bufferedAhead === 'number' &&
        typeof this.player?.currentTime === 'number'
      ) {
        reportGodavariObservation(reporter, 'BufferHealthSample', {
          position: this.player.currentTime,
          bufferedAhead: observation.statistics.bufferedAhead,
        });
      }
      this.godavariObserver.onProgress(reporter, this.godavariProgressSnapshot(observation));
    }
  };

  private readonly onManifestParsedForGodavari = (): void => {
    const reporter = this.reporters[Provider.GODAVARI];
    if (reporter && this.isMainContext(this.player)) {
      this.reportGodavariPlayerMetadata(reporter);
      reportGodavariObservation(reporter, 'ManifestParsed');
    }
  };

  private readonly onNetworkRequestForGodavari = (observation: NetworkRequestObservation): void => {
    const reporter = this.reporters[Provider.GODAVARI];
    if (reporter && this.isMainContext(this.player)) {
      reportGodavariObservation(reporter, 'NetworkRequest', observation);
    }
  };

  private readonly onNetworkResponseForGodavari = (observation: NetworkRequestObservation): void => {
    const reporter = this.reporters[Provider.GODAVARI];
    if (reporter && this.isMainContext(this.player)) {
      reportGodavariObservation(reporter, 'NetworkResponse', observation);
    }
  };

  private reportGodavariPlayerState(reporter: AnalyticsReporter): void {
    switch (this.player?.playerState) {
      case PLAYBACK_STATES.LOADING:
        reportGodavariObservation(reporter, 'VideoAttempt');
        break;
      case PLAYBACK_STATES.LOADED:
        this.onPlaybackLoaded(reporter);
        break;
      case PLAYBACK_STATES.STARTED:
        this.onPlaybackStarted(reporter);
        break;
    }
  }

  private readonly onAudioTrackChanged = (): void => {
    const activeAudioTrack = this.player?.getActiveTracks().find(track => track.type === TrackType.AUDIO);
    if (activeAudioTrack?.language) {
      this.reportTrackChange(
        PlaybackEvent.PLAYBACK_AUDIO_LANGUAGE_CHANGE,
        PlaybackDataKey.AUDIO_LANGUAGE,
        activeAudioTrack.language,
      );
    }
    const reporter = this.reporters[Provider.GODAVARI];
    if (reporter && this.isMainContext(this.player)) {
      this.reportGodavariPlayerMetadata(reporter);
    }
  };

  private reportGodavariPlayerMetadata(
    reporter: AnalyticsReporter,
    observation?: { statistics: PlaybackStatistics | undefined },
  ): void {
    reporter.reportMetadata(this.buildContentMetadata(reporter, this.content, observation));
  }

  private readonly onTextTracksChanged = (): void => {
    const activeTextTrack = this.player?.getActiveTracks().find(track => track.type === TrackType.TEXT);
    if (activeTextTrack?.language) {
      this.reportTrackChange(
        PlaybackEvent.PLAYBACK_SUBTITLE_LANGUAGE_CHANGE,
        PlaybackDataKey.SUBTITLE_LANGUAGE,
        activeTextTrack.language,
      );
    } else {
      const reporter = this.reporters[Provider.GODAVARI];
      reporter?.reportEvent(PlaybackEvent.PLAYBACK_SUBTITLE_LANGUAGE_CHANGE, { video_subtitle_language: 'off' });
    }
    const reporter = this.reporters[Provider.GODAVARI];
    if (reporter && this.isMainContext(this.player)) {
      this.reportGodavariPlayerMetadata(reporter);
    }
  };

  private readonly reportTrackChange = (event: PlaybackEvent, dataKey: PlaybackDataKey, language: string): void => {
    Object.values(this.reporters).forEach((reporter: AnalyticsReporter) => {
      if (reporter.provider === Provider.NEWRELIC) {
        reporter.reportEvent(event, {
          ...this.buildNewRelicContentMetadata(this.content),
          [dataKey]: language,
        });
      } else if (reporter.provider === Provider.GODAVARI) {
        reporter.reportEvent(event, { [dataKey]: language });
      }
    });
  };

  private readonly onStreamTimelineEvent = (eventName: StreamTimelineEvent, _action: Action, _metadata?: any): void => {
    const resetProgramInfo = () => {
      this.content.programName = undefined;
      this.content.programId = undefined;
    };

    switch (eventName) {
      case StreamTimelineEvent.LIVE_PROGRAM_START:
        resetProgramInfo();
        this.programPlaybackSessionId = uuid();
        break;

      case StreamTimelineEvent.LIVE_PROGRAM_END:
        resetProgramInfo();
        this.programPlaybackSessionId = undefined;
        break;
    }
    const reporter = this.reporters[Provider.GODAVARI];
    if (reporter && this.isMainContext(this.player)) {
      this.reportGodavariPlayerMetadata(reporter);
    }
  };

  /**
   * Listens for playback state changes
   * @param playbackState the new playback state
   */
  private readonly onPlaybackStateChanged = (playbackState: PLAYBACK_STATES): void => {
    if (!this.reporters) {
      return;
    }
    Object.keys(this.reporters).forEach((key: string) => {
      const reporter = this.reporters[key];
      const observation =
        reporter.provider === Provider.GODAVARI &&
          (this.isMainContext(this.player) || playbackState === PLAYBACK_STATES.STARTED)
          ? { statistics: this.player?.playbackStatistics }
          : undefined;

      if (reporter.provider === Provider.GODAVARI && this.isMainContext(this.player)) {
        reporter.reportMetadata(this.buildContentMetadata(reporter, this.content, observation));
      }
      if (reporter.provider === Provider.DATAZOOM) {
        reporter.reportMetadata(this.buildMetadata(), this.playerContext);
        return;
      }

      switch (playbackState) {
        case PLAYBACK_STATES.LOADING:
          if (reporter.provider === Provider.GODAVARI) {
            reportGodavariObservation(reporter, 'VideoAttempt');
          }
          break;
        case PLAYBACK_STATES.LOADED:
          this.onPlaybackLoaded(reporter);
          break;
        case PLAYBACK_STATES.STARTED: {
          if (reporter.provider !== Provider.GODAVARI) {
            this.startProgressInterval(reporter);
          }
          this.onPlaybackStarted(reporter, observation);
          break;
        }
        case PLAYBACK_STATES.PAUSED: {
          this.clearProgressInterval(reporter);
          this.onPlaybackPaused(reporter);
          break;
        }
        case PLAYBACK_STATES.STOPPED: {
          this.contentStarted = false;
          this.clearProgressInterval(reporter);
          this.onPlaybackStopped(reporter);
          break;
        }
        case PLAYBACK_STATES.IDLE: {
          this.contentStarted = false;
          this.clearProgressInterval(reporter);
          this.onPlaybackIdle(reporter);
          break;
        }
      }
    });

    // Defer stop() until all reporters have completed processing the IDLE transition.
    // Invoking stop() inside the reporter iteration clears playerContext and unsubscribes
    // player events immediately, which can cause subsequent reporters (e.g. Datazoom)
    // to receive a null playerContext during reportMetadata execution.
    if (playbackState === PLAYBACK_STATES.IDLE) {
      this.stop();
    }
  };

  private onPlaybackLoaded(reporter: AnalyticsReporter): void {
    switch (reporter.provider) {
      case Provider.GODAVARI:
        reporter.reportEvent(PlaybackEvent.PLAYBACK_PREPARED, this.buildMetadata());
        break;

      case Provider.NPAW: {
        const videoMetadata = this.buildNPAWVideoMetadata();
        reporter.reportMetadata(videoMetadata);
        break;
      }

      case Provider.NEWRELIC:
        reporter.reportEvent(PlaybackEvent.PLAYBACK_PREPARED, this.buildNewRelicContentMetadata(this.content));
        break;
    }
  }

  private startProgressInterval(reporter: AnalyticsReporter): void {
    this.clearProgressInterval(reporter); // prevent duplicates for same reporter

    const progressIntervalRef = setInterval(
      () => this.onProgressUpdate(reporter), // pass reporter explicitly
      this.progressUpdateIntervalsInMs?.[reporter.provider] ?? this.DEFAULT_PROGRESS_UPDATE_INTERVAL_MS,
    );

    this.progressIntervalRefs = {
      ...this.progressIntervalRefs,
      [reporter.provider]: progressIntervalRef,
    };
  }

  private clearProgressInterval(reporter: AnalyticsReporter): void {
    const ref = this.progressIntervalRefs?.[reporter.provider];
    if (!ref) return;

    clearInterval(ref);
    if (this.progressIntervalRefs) {
      this.progressIntervalRefs[reporter.provider] = undefined;
    }
  }

  private onPlaybackStarted(
    reporter: AnalyticsReporter,
    observation?: { statistics: PlaybackStatistics | undefined },
  ): void {
    if (reporter.provider === Provider.GODAVARI) {
      this.godavariObserver.onPlaybackStarted(
        reporter,
        this.godavariProgressSnapshot(observation),
        this.isMainContext(this.player) && !this.isAdBreakStartReported,
        () => this.buildMetadata(),
      );
      return;
    }
    if (!this.restrictedProviders.includes(reporter.provider) || !this.isMainContext(this.player)) {
      return;
    }
    this.calculatePlaybackStartupTimeIfNeeded();

    switch (reporter.provider) {
      case Provider.CONVIVA:
        this.handleConvivaPlaybackStarted(reporter);
        break;

      case Provider.NEWRELIC:
        // Do not emit PLAYBACK_STARTED here.
        //
        // The initial PLAYBACK_STARTED is deferred to `onProgressForPlaybackStart` to ensure
        // vstStartMs and vsLicenseMs are fully populated and to avoid reporting while buffering.
        // This handler should only emit RESUMED events for playback that has already started.
        if (this.contentStarted) {
          this.handleNewRelicPlaybackStarted(reporter);
        }
        break;
    }
  }

  /**
   * Conviva-specific playback started handling
   */
  private handleConvivaPlaybackStarted(reporter: AnalyticsReporter): void {
    if (!this.player) return;

    // Start cost optimization timeout
    if (this.player.isLive && this.liveThresholdMs !== 0) {
      this.startTimeout(this.liveThresholdMs);
    } else if (!this.player.isLive && this.vodThresholdMs !== 0) {
      this.startTimeout(this.vodThresholdMs);
    }

    // Report basic session metadata
    reporter.reportMetadata({
      [ConvivaConstants.IS_LIVE]: isLive(this.player),
    });

    // Note:
    // During the start of the playback when the player moves to started state and the buffering state is still active.
    // For this scenario, we should report the playerState as PLAYING to conviva unless the buffering ends.

    if (this.bufferingState !== BUFFERING_STATES.ACTIVE) {
      this.reportMetric(reporter, ConvivaConstants.Playback.PLAYER_STATE, ConvivaConstants.PlayerState.PLAYING);
    }

    // Report playback statistics (bitrate & framerate)
    const stats = this.player.playbackStatistics;
    if (stats) {
      if (typeof stats.variantBitrate === 'number' && stats.variantBitrate > 0) {
        // Conviva expects kbps
        this.reportMetric(reporter, ConvivaConstants.Playback.BITRATE, stats.variantBitrate / 1000);
      }

      if (typeof stats.framerate === 'number' && stats.framerate > 0) {
        this.reportMetric(reporter, ConvivaConstants.Playback.RENDERED_FRAMERATE, stats.framerate);
      }
    }
  }

  /**
   * New Relic-specific playback started handling
   */
  private handleNewRelicPlaybackStarted(reporter: AnalyticsReporter): void {
    if (!this.contentStarted) {
      reporter.reportEvent(PlaybackEvent.PLAYBACK_STARTED, {
        ...this.buildNewRelicContentMetadata(this.content),
        [PlaybackDataKey.PLAYBACK_STARTED_TIME_SEC]: this.playbackStartedTimeSec,
        [PlaybackDataKey.AUTO_PREVIEW]: this.content.isAutoPreview,
      });
    } else {
      reporter.reportEvent(PlaybackEvent.PLAYBACK_RESUMED, {
        ...this.buildNewRelicContentMetadata(this.content),
        [PlaybackDataKey.CURRENT_POSITION]: Math.ceil(this.player?.currentTime ?? 0),
      });
    }
  }

  /**
   * Helper to report a Conviva metric safely
   */
  private reportMetric(reporter: AnalyticsReporter, key: string, value: number | string): void {
    if (value !== undefined && value !== null) {
      reporter.reportEvent(PlaybackEvent.PLAYBACK_METRIC, { key, value });
    }
  }

  private onPlaybackPaused(reporter: AnalyticsReporter): void {
    if (reporter.provider === Provider.GODAVARI) {
      this.godavariObserver.onPaused(this.isMainContext(this.player));
      reporter.reportEvent(
        this.isMainContext(this.player) ? PlaybackEvent.PLAYBACK_PAUSED : AdPlaybackEvent.AD_PAUSED_EVENT,
        this.isMainContext(this.player) ? this.buildMetadata() : {},
      );
      return;
    }
    if (!this.restrictedProviders.includes(reporter.provider)) {
      return;
    }

    switch (reporter.provider) {
      case Provider.CONVIVA:
        // Stop timeout for cost optimization

        if (!this.isMainContext(this.player)) {
          reporter.reportEvent(AdPlaybackEvent.AD_PLAYBACK_METRIC, {
            key: ConvivaConstants.Playback.PLAYER_STATE,
            value: ConvivaConstants.PlayerState.PAUSED,
          });

          return;
        }

        reporter.reportEvent(PlaybackEvent.PLAYBACK_METRIC, {
          key: ConvivaConstants.Playback.PLAYER_STATE,
          value: ConvivaConstants.PlayerState.PAUSED,
        });

        if (!this.player?.isLive) {
          reporter.reportEvent(PlaybackEvent.PLAYBACK_METRIC, {
            key: ConvivaConstants.Playback.PLAY_HEAD_TIME,
            value: this.player?.currentTime,
          });
        }
        break;
      case Provider.NEWRELIC:
        reporter.reportEvent(PlaybackEvent.PLAYBACK_PAUSED, {
          ...this.buildNewRelicContentMetadata(this.content),
          [PlaybackDataKey.IS_FREE_PREVIEW]: this.content.isFreePreview,
          [PlaybackDataKey.CURRENT_POSITION]: Math.ceil(this.player?.currentTime ?? 0),
          [UserJourneyDataKey.SEARCH_ITEM_POSITION]: this.content.searchItemPosition,
        });
        break;
    }
  }

  private onPlaybackStopped(reporter: AnalyticsReporter): void {
    if (!this.restrictedProviders.includes(reporter.provider)) {
      return;
    }

    switch (reporter.provider) {
      case Provider.NEWRELIC:
        reporter.reportEvent(PlaybackEvent.PLAYBACK_STOPPED, {
          ...this.buildNewRelicContentMetadata(this.content),
          [PlaybackDataKey.CURRENT_POSITION]: Math.ceil(this.player?.currentTime ?? 0),
          [PlaybackDataKey.AUTO_PREVIEW]: this.content.isAutoPreview,
          [PlaybackDataKey.IS_FREE_PREVIEW]: this.content.isFreePreview,
        });
        break;

      case Provider.CONVIVA:
        reporter.reportEvent(PlaybackEvent.PLAYBACK_METRIC, {
          key: ConvivaConstants.Playback.PLAYER_STATE,
          value: ConvivaConstants.PlayerState.STOPPED,
        });

        if (!this.player?.isLive) {
          reporter.reportEvent(PlaybackEvent.PLAYBACK_METRIC, {
            key: ConvivaConstants.Playback.PLAY_HEAD_TIME,
            value: this.player?.currentTime,
          });
        }
        break;

      default:
        break;
    }
  }

  private readonly onPlaybackIdle = (reporter: AnalyticsReporter): void => {
    if (reporter.provider !== Provider.GODAVARI && !this.restrictedProviders.includes(reporter.provider)) {
      return;
    }

    switch (reporter.provider) {
      case Provider.GODAVARI:
        break;

      case Provider.CONVIVA:
        reporter.reportEvent(PlaybackEvent.PLAYBACK_END);

        if (!this.player?.isLive) {
          reporter.reportEvent(PlaybackEvent.PLAYBACK_METRIC, {
            key: ConvivaConstants.Playback.PLAYER_STATE,
            value: ConvivaConstants.PlayerState.PAUSED,
          });

          if (!this.player?.isLive) {
            reporter.reportEvent(PlaybackEvent.PLAYBACK_METRIC, {
              key: ConvivaConstants.Playback.PLAY_HEAD_TIME,
              value: this.player?.currentTime,
            });
          }
        }
        break;

      case Provider.NEWRELIC:
        reporter.reportEvent(PlaybackEvent.PLAYBACK_STOPPED, {
          ...this.buildNewRelicContentMetadata(this.content),
          [PlaybackDataKey.CURRENT_POSITION]: Math.ceil(this.player?.currentTime ?? 0),
          [PlaybackDataKey.AUTO_PREVIEW]: this.content.isAutoPreview,
          [PlaybackDataKey.IS_FREE_PREVIEW]: this.content.isFreePreview,
        });
        break;

      default: {
        this.loggers?.[reporter.provider]?.log(`[ANALYTICS] Unknown provider in onPlaybackIdle: ${reporter.provider}`);
        break;
      }
    }

    // Unsubscribe from player events regardless of provider
    this.unsubscribeFromPlayerEvents(this.player);
  };

  /**
   * Listens for seeking state changes.
   * @param seekingState the new seeking state
   */
  private readonly onSeekingStateChanged = (seekingState: SEEKING_STATES): void => {
    Object.values(this.reporters).forEach((reporter: AnalyticsReporter) => {
      if (reporter.provider === Provider.DATAZOOM) {
        reporter.reportMetadata(this.buildMetadata(), this.playerContext);
        return;
      }
      switch (seekingState) {
        case SEEKING_STATES.ACTIVE: {
          if (reporter.provider === Provider.CONVIVA) {
            reporter.reportEvent(PlaybackEvent.PLAYBACK_METRIC, { key: ConvivaConstants.Playback.SEEK_STARTED });
          }
          if (reporter.provider === Provider.NEWRELIC) {
            reporter.reportEvent(PlaybackEvent.PLAYBACK_SEEK_START, this.buildNewRelicContentMetadata(this.content));
          }
          if (reporter.provider === Provider.GODAVARI) {
            reporter.reportMetadata(this.buildContentMetadata(reporter, this.content));
            reporter.reportEvent(PlaybackEvent.PLAYBACK_SEEK_START, this.buildMetadata());
          }
          break;
        }
        case SEEKING_STATES.INACTIVE: {
          if (reporter.provider === Provider.CONVIVA) {
            reporter.reportEvent(PlaybackEvent.PLAYBACK_METRIC, { key: ConvivaConstants.Playback.SEEK_ENDED });
          }
          if (reporter.provider === Provider.NEWRELIC) {
            reporter.reportEvent(PlaybackEvent.PLAYBACK_SEEK_COMPLETE, this.buildNewRelicContentMetadata(this.content));
          }
          if (reporter.provider === Provider.GODAVARI) {
            reporter.reportMetadata(this.buildContentMetadata(reporter, this.content));
            reporter.reportEvent(PlaybackEvent.PLAYBACK_SEEK_COMPLETE, this.buildMetadata());
          }
          break;
        }
      }
    });
  };

  /**
   * Listens for buffering state changes.
   * @param bufferingState the new buffering state
   */
  private readonly onBufferingStateChanged = (bufferingState: BUFFERING_STATES): void => {
    Object.values(this.reporters).forEach((reporter: AnalyticsReporter) => {
      this.reportBufferingState(reporter, bufferingState);
    });
  };

  private reportBufferingState(reporter: AnalyticsReporter, bufferingState: BUFFERING_STATES): void {
    switch (reporter.provider) {
      case Provider.DATAZOOM:
        reporter.reportMetadata(this.buildMetadata(), this.playerContext);
        break;
      case Provider.CONVIVA:
        this.reportConvivaBufferingState(reporter, bufferingState);
        break;
      case Provider.NEWRELIC:
        this.reportNewRelicBufferingState(reporter, bufferingState);
        break;
      case Provider.GODAVARI:
        this.reportGodavariBufferingState(reporter, bufferingState);
        break;
    }
  }

  private reportConvivaBufferingState(reporter: AnalyticsReporter, bufferingState: BUFFERING_STATES): void {
    this.bufferingState = bufferingState;
    let stateValue: string;
    if (bufferingState === BUFFERING_STATES.ACTIVE) {
      stateValue = ConvivaConstants.PlayerState.BUFFERING;
    } else if (this.player?.playerState === PLAYBACK_STATES.PAUSED) {
      stateValue = ConvivaConstants.PlayerState.PAUSED;
    } else {
      stateValue = ConvivaConstants.PlayerState.PLAYING;
    }
    reporter.reportEvent(PlaybackEvent.PLAYBACK_METRIC, {
      key: ConvivaConstants.Playback.PLAYER_STATE,
      value: stateValue,
    });
  }

  private reportNewRelicBufferingState(reporter: AnalyticsReporter, bufferingState: BUFFERING_STATES): void {
    const eventName =
      bufferingState === BUFFERING_STATES.ACTIVE
        ? PlaybackEvent.PLAYBACK_BUFFERING_START
        : PlaybackEvent.PLAYBACK_BUFFERING_END;
    reporter.reportEvent(eventName, {
      ...this.buildNewRelicContentMetadata(this.content),
      [PlaybackDataKey.AUTO_PREVIEW]: this.content.isAutoPreview,
    });
  }

  private reportGodavariBufferingState(reporter: AnalyticsReporter, bufferingState: BUFFERING_STATES): void {
    const isMainContext = this.isMainContext(this.player);
    let eventName: string;
    if (isMainContext && bufferingState === BUFFERING_STATES.ACTIVE) {
      eventName = PlaybackEvent.PLAYBACK_BUFFERING_START;
    } else if (isMainContext) {
      eventName = PlaybackEvent.PLAYBACK_BUFFERING_END;
    } else if (bufferingState === BUFFERING_STATES.ACTIVE) {
      eventName = 'AdBufferStart';
    } else {
      eventName = 'AdBufferEnd';
    }
    reporter.reportMetadata(this.buildContentMetadata(reporter, this.content));
    reporter.reportEvent(eventName, isMainContext ? this.buildMetadata() : {});
  }

  /**
   * Listens for progress update event.
   */
  private readonly onProgressUpdate = (reporter: AnalyticsReporter): void => {
    switch (reporter.provider) {
      case Provider.DATAZOOM:
        reporter.reportMetadata(this.buildMetadata(), this.playerContext);
        break;

      case Provider.CONVIVA:
        if (!this.player?.duration || this.player?.isLive) {
          return;
        }

        reporter.reportMetadata({
          [ConvivaConstants.DURATION]: this.player.duration,
        });

        reporter.reportEvent(PlaybackEvent.PLAYBACK_METRIC, {
          key: ConvivaConstants.Playback.PLAY_HEAD_TIME,
          value: this.player.currentTime * 1_000,
        });

        reporter.reportEvent(PlaybackEvent.PLAYBACK_METRIC, {
          key: ConvivaConstants.Playback.DROPPED_FRAMES_TOTAL,
          value: this.player.playbackStatistics?.droppedFrames,
        });

        reporter.reportEvent(PlaybackEvent.PLAYBACK_METRIC, {
          key: ConvivaConstants.Playback.RENDERED_FRAMERATE,
          value: this.player.playbackStatistics?.framerate,
        });
        break;

      case Provider.NEWRELIC:
        if (this.isAdBreakStartReported) {
          return;
        }
        reporter.reportEvent(PlaybackEvent.PLAYBACK_INPROGRESS, {
          ...this.buildNewRelicContentMetadata(this.content),
          [PlaybackDataKey.AUTO_PREVIEW]: this.content.isAutoPreview,
          [PlaybackDataKey.CURRENT_POSITION]: Math.ceil(this.player?.currentTime ?? 0),
        });
        break;
    }
  };

  /**
   * Listens for advert break start.
   */
  private readonly onAdBreakStart = (adBreakInfo?: AdBreakInfo | AdBreak): void => {
    if (this.isAdBreakStartReported) return;

    this.updateGodavariWatchTime(this.player?.playbackStatistics);
    this.isAdBreakStartReported = true;
    this.isAdBreakEndReported = false;
    this.activeAdBreak = adBreakInfo;

    // IMA CSAI may report ad break start before playback reaches the STARTED state.
    // If the playback start timestamp has not been recorded yet, calculate and set it here.
    this.calculatePlaybackStartupTimeIfNeeded();

    const position = (adBreakInfo as any)?.contentTimePosition ?? (adBreakInfo as any)?.position ?? 'unknown';

    const currentPosition = Math.ceil(this.player?.currentTime ?? 0);

    Object.values(this.reporters).forEach((reporter: AnalyticsReporter) => {
      switch (reporter.provider) {
        case Provider.NPAW:
          return;

        case Provider.NEWRELIC: {
          reporter.reportEvent(AdPlaybackEvent.AD_BREAK_START, {
            ...this.buildNewRelicContentMetadata(this.content),
            [AdDataKey.AD_TYPE]: position,
            [UserJourneyDataKey.CURRENT_SCREEN]: this.content?.currentScreen ?? 'unknown',
            [PlaybackDataKey.CURRENT_POSITION]: currentPosition,
            [PlaybackDataKey.IS_FREE_PREVIEW]: this.content.isFreePreview,
          });
          return;
        }

        default:
          reporter.reportEvent(AdPlaybackEvent.AD_BREAK_START, undefined, this.playerContext);
          break;
      }
    });
  };

  /**
   * Listens for advert break end.
   */
  private readonly onAdBreakEnd = (adBreakInfo?: AdBreakInfo | AdBreak): void => {
    if (this.isAdBreakEndReported) return;

    this.updateGodavariWatchTime(this.player?.playbackStatistics);
    this.isAdBreakStartReported = false;
    this.isAdBreakEndReported = true;
    const currentAd = adBreakInfo ?? this.activeAdBreak;

    const position = (currentAd as any)?.contentTimePosition ?? (currentAd as any)?.position ?? 'unknown';

    Object.values(this.reporters).forEach((reporter: AnalyticsReporter) => {
      switch (reporter.provider) {
        case Provider.NPAW:
          return;

        case Provider.NEWRELIC: {
          reporter.reportEvent(AdPlaybackEvent.AD_BREAK_END, {
            ...this.buildNewRelicContentMetadata(this.content),
            [AdDataKey.AD_TYPE]: position,
            [UserJourneyDataKey.CURRENT_SCREEN]: this.content?.currentScreen ?? 'unknown',
            [PlaybackDataKey.IS_FREE_PREVIEW]: this.content.isFreePreview,
          });
          return;
        }

        default:
          reporter.reportEvent(AdPlaybackEvent.AD_BREAK_END, undefined, this.playerContext);
          break;
      }
    });

    this.activeAdBreak = undefined;
  };

  private readonly onAdRequestStart = (): void => {
    this.reporters[Provider.GODAVARI]?.reportEvent('vmaprequeststart');
  };

  /**
   * Listens for the advert request to complete.
   * @param result whether the ad server returned adverts
   */
  private readonly onAdRequestEnd = (result?: { success?: boolean }): void => {
    this.reporters[Provider.GODAVARI]?.reportEvent('vmaprequestend', {
      vmap_success: result?.success === false ? 'false' : 'true',
    });
  };

  /**
   * Listens for an advert to load. IMA also loads mid and post-roll adverts ahead of their break, so the
   * Godavari ad attempt is reported when the advert starts or its impression arrives rather than here.
   * @param adInfo advert information
   */
  private readonly onAdLoaded = (adInfo: AdInfo | Ad): void => {
    this.loadedAd = adInfo;
    const reporter = this.reporters[Provider.GODAVARI];
    if (reporter) {
      reporter.reportMetadata(this.buildContentMetadata(reporter, this.content));
    }
  };

  /**
   * Listens for advert start.
   * @param adInfo advert information
   */
  private readonly onAdStart = (adInfo: AdInfo | Ad): void => {
    const framerate = this.player?.playbackStatistics?.framerate;
    this.activeAd = adInfo;

    Object.values(this.reporters).forEach((reporter: AnalyticsReporter) => {
      if (reporter.provider === Provider.NPAW) {
        return;
      }
      const metadata =
        reporter.provider === Provider.DATAZOOM || reporter.provider === Provider.NEWRELIC
          ? this.buildAdMetadata(adInfo, reporter.provider)
          : adInfo;

      if (reporter.provider === Provider.GODAVARI) {
        reporter.reportEvent(AdPlaybackEvent.AD_START, metadata);
        return;
      }

      if (reporter.provider === Provider.NEWRELIC) {
        reporter.reportEvent(AdPlaybackEvent.AD_START, {
          ...this.buildNewRelicContentMetadata(this.content),
          ...metadata,
          [PlaybackDataKey.CURRENT_POSITION]: Math.ceil(this.player?.currentTime ?? 0),
          [UserJourneyDataKey.CURRENT_SCREEN]: this.content.currentScreen,
          [PlaybackDataKey.IS_FREE_PREVIEW]: this.content.isFreePreview,
        });
        return;
      }

      reporter.reportEvent(AdPlaybackEvent.AD_START, metadata, this.playerContext);

      // Conviva (and others except New Relic) need framerate metrics
      if (framerate) {
        const frameRateMetric = {
          key: ConvivaConstants.Playback.RENDERED_FRAMERATE,
          value: framerate,
        };

        reporter.reportEvent(PlaybackEvent.PLAYBACK_METRIC, frameRateMetric);
        reporter.reportEvent(AdPlaybackEvent.AD_PLAYBACK_METRIC, frameRateMetric);
      }
    });
  };

  /**
   * Listens for advert end.
   * @param adInfo advert information
   */
  private readonly onAdEnd = (adInfo: AdInfo | Ad): void => {
    Object.values(this.reporters).forEach((reporter: AnalyticsReporter) => {
      if (reporter.provider === Provider.NPAW) {
        return;
      }
      const metadata =
        reporter.provider === Provider.DATAZOOM || reporter.provider === Provider.NEWRELIC
          ? this.buildAdMetadata(adInfo, reporter.provider)
          : adInfo;

      if (reporter.provider === Provider.NEWRELIC) {
        reporter.reportEvent(AdPlaybackEvent.AD_END, {
          ...this.buildNewRelicContentMetadata(this.content),
          ...metadata,
          [PlaybackDataKey.CURRENT_POSITION]: Math.ceil(this.player?.currentTime ?? 0),
          [UserJourneyDataKey.CURRENT_SCREEN]: this.content.currentScreen,
          [PlaybackDataKey.IS_FREE_PREVIEW]: this.content.isFreePreview,
        });
        return;
      }
      reporter.reportEvent(AdPlaybackEvent.AD_END, metadata, this.playerContext);
    });
    this.activeAd = undefined;
  };
  /**
   * Listens for advert skip.
   * @param adInfo advert information
   */
  private readonly onAdSkip = (adInfo: AdInfo | Ad): void => {
    Object.values(this.reporters).forEach((reporter: AnalyticsReporter) => {
      if (reporter.provider === Provider.NPAW) {
        return;
      }
      const metadata = this.buildAdMetadata(adInfo, reporter.provider);

      if (reporter.provider === Provider.NEWRELIC) {
        reporter.reportEvent(AdPlaybackEvent.AD_SKIP, {
          ...this.buildNewRelicContentMetadata(this.content),
          ...metadata,
          [PlaybackDataKey.IS_FREE_PREVIEW]: this.content.isFreePreview,
        });
        return;
      }
      reporter.reportEvent(AdPlaybackEvent.AD_SKIP, metadata, this.playerContext);
    });
  };

  /**
   * Ad errors can arrive without ad break data, so the break position is inferred from the content playhead.
   */
  private resolveAdBreak(adBreak?: AdBreakInfo | AdBreak): AdBreakInfo | AdBreak | { position: string } {
    const known = adBreak as any;
    const isKnown = (position?: string) => !!position && position !== AdContentTimePosition.UNKNOWN;
    if (isKnown(known?.contentTimePosition) || isKnown(known?.position)) {
      return adBreak as AdBreakInfo | AdBreak;
    }
    const currentTime = this.player?.currentTime ?? 0;
    const duration = this.player?.duration;
    const isPostRoll = !this.player?.isLive && !!duration && Number.isFinite(duration) && currentTime >= duration - 1;
    let position = AdContentTimePosition.MIDROLL;
    if (currentTime <= 1) {
      position = AdContentTimePosition.PREROLL;
    } else if (isPostRoll) {
      position = AdContentTimePosition.POSTROLL;
    }
    // contentTimePosition is preferred during normalization, so both representations are set.
    return { ...known, position, contentTimePosition: position };
  }

  /**
   * Listens for an ad break skipped by the ad frequency control (AFC / TrueView) policy.
   * @param adBreakInfo ad break information
   */
  private readonly onAdSkipAfcPolicy = (adBreakInfo?: AdBreakInfo | AdBreak): void => {
    const breakInfo = this.resolveAdBreak(adBreakInfo) as any;
    const metadata = {
      adBreakInfo: breakInfo,
      adBreak: breakInfo,
      adBreakId: breakInfo?.adBreakID ?? breakInfo?.id,
      adBreakPosition: breakInfo?.contentTimePosition ?? breakInfo?.position,
      adBreakStartTimeOffsetMs: breakInfo?.adBreakStartTimeOffsetMs,
    };
    Object.values(this.reporters).forEach((reporter: AnalyticsReporter) => {
      if (reporter.provider === Provider.GODAVARI) {
        reporter.reportEvent(AdPlaybackEvent.AD_SKIP_AFC_POLICY, metadata);
      }
    });
  };

  private readonly onAdProgressUpdate = (activeAdInfo: AdInfo, _progress: number): void => {
    Object.values(this.reporters).forEach((reporter: AnalyticsReporter) => {
      if (reporter.provider === Provider.GODAVARI) {
        const duration = activeAdInfo?.durationMs;
        const remaining = activeAdInfo?.remainingTimeMs;
        if (
          typeof duration === 'number' &&
          Number.isFinite(duration) &&
          typeof remaining === 'number' &&
          Number.isFinite(remaining) &&
          duration >= 0 &&
          remaining >= 0 &&
          remaining <= duration
        ) {
          const watchedSeconds = (duration - remaining) / 1000;
          reporter.reportMetadata({
            ads_length_watched: watchedSeconds,
            ads_watch_time_duration: watchedSeconds,
          });
        } else {
          this.loggers?.[Provider.GODAVARI]?.warn(
            'Godavari ad progress requires duration and remaining time in milliseconds.',
          );
        }
        return;
      }
      const restrictedProvidersForAdProgress = [Provider.NPAW, Provider.NEWRELIC];
      if (!restrictedProvidersForAdProgress.includes(reporter.provider)) {
        reporter.reportMetadata(this.buildMetadata(), this.playerContext);
        reporter.reportEvent(AdPlaybackEvent.AD_PLAYBACK_METRIC, {
          key: ConvivaConstants.Playback.PLAYER_STATE,
          value: ConvivaConstants.PlayerState.PLAYING,
        });

        reporter.reportEvent(AdPlaybackEvent.AD_PLAYBACK_METRIC, {
          key: ConvivaConstants.Playback.DROPPED_FRAMES_TOTAL,
          value: this.player?.playbackStatistics?.droppedFrames,
        });
      }
    });
  };

  private readonly onAdTracking = (adTrackingEvent: string): void => {
    if (!this.reporters || !this.content) {
      return;
    }

    const isNewRelicReporter = (reporter: AnalyticsReporter): boolean => reporter.provider === Provider.NEWRELIC;

    const metadata: Record<string, any> = this.activeAd ? this.buildAdMetadata(this.activeAd, Provider.NEWRELIC) : {};

    const baseMetadata = {
      ...this.buildNewRelicContentMetadata(this.content),
      [PlaybackDataKey.IS_FREE_PREVIEW]: this.content.isFreePreview,
      [UserJourneyDataKey.CURRENT_SCREEN]: this.content.currentScreen,
    };

    for (const reporter of Object.values(this.reporters)) {
      if (isNewRelicReporter(reporter)) {
        this.handleAdTrackingEvent(adTrackingEvent, reporter, metadata, baseMetadata);
      } else if (reporter.provider === Provider.GODAVARI && adTrackingEvent === AdTrackingEvent.AD_IMPRESSION) {
        reporter.reportEvent(AdPlaybackEvent.AD_IMPRESSION, this.activeAd || this.loadedAd || {});
      } else if (reporter.provider === Provider.GODAVARI && adTrackingEvent === AdTrackingEvent.AD_CLICK) {
        reporter.reportEvent(AdPlaybackEvent.AD_VIEW, {
          heart_beat_event_ad_click_url: this.activeAd?.vastProperties?.videoClickThroughURL || '',
        });
      }
    }
  };

  /**
   * Handles specific ad tracking events for New Relic reporters.
   */
  private handleAdTrackingEvent(
    adTrackingEvent: string,
    reporter: AnalyticsReporter,
    metadata: Record<string, any>,
    baseMetadata: Record<string, any>,
  ): void {
    switch (adTrackingEvent) {
      case AdTrackingEvent.AD_SKIPPED_EVENT:
        reporter.reportEvent(AdPlaybackEvent.AD_SKIP, {
          ...baseMetadata,
          ...metadata,
        });
        break;

      case AdTrackingEvent.AD_CLICK:
        reporter.reportEvent(AdPlaybackEvent.AD_VIEW, {
          ...baseMetadata,
          [AdDataKey.AD_TYPE]: metadata[AdDataKey.AD_TYPE],
        });
        break;

      default:
        this.loggers?.[reporter.provider]?.log(`[AdTracking] Unhandled event: ${adTrackingEvent}`);
        break;
    }
  }

  private readonly onBandwidthChanged = (): void => {
    const statistics = this.player?.playbackStatistics;
    if (!statistics) {
      return;
    }

    const { variantBitrate, lastPlayedVariantBandwidth } = statistics;
    const bitrateKbps = variantBitrate ? variantBitrate / 1000 : 0;

    Object.values(this.reporters).forEach((reporter: AnalyticsReporter) => {
      switch (reporter.provider) {
        case Provider.NEWRELIC:
          reporter.reportEvent(PlaybackEvent.PLAYBACK_VARIANT_CHANGE, {
            ...this.buildNewRelicContentMetadata(this.content),
            [PlaybackDataKey.IS_FREE_PREVIEW]: this.content.isFreePreview,
            [PlaybackDataKey.LAST_PLAYED_VARIANT]: lastPlayedVariantBandwidth,
          });
          break;
        case Provider.GODAVARI:
          if (this.isMainContext(this.player)) {
            this.reportGodavariPlayerMetadata(reporter, { statistics });
          }
          reporter.reportEvent(PlaybackEvent.PLAYBACK_VARIANT_CHANGE, {
            video_current_bitrate: bitrateKbps,
            estimated_bandwidth_in_kbbs: (statistics.estimatedBandwidth ?? 0) / 1000,
          });
          break;
        case Provider.CONVIVA:
          reporter.reportEvent(PlaybackEvent.PLAYBACK_METRIC, {
            key: ConvivaConstants.Playback.BITRATE,
            value: bitrateKbps,
          });
          break;

        case Provider.DATAZOOM:
          // No reporting for Datazoom in this handler
          break;

        case Provider.NPAW:
          // No reporting for NPAW in this handler
          break;
      }
    });
  };

  /*
   * Listens for errors.
   * @param error the error
   */
  private readonly onError = (error: Error): void => {
    if (this.loggers) {
      Object.values(this.loggers).forEach((logger: Logger) => logger?.log('[ANALYTICS] playback error', error));
    }

    const playbackError = {
      [ApplicationErrorDataKey.APP_ERROR_CODE]: error.hexErrorCode,
      [ApplicationErrorDataKey.APP_ERROR_MESSAGE]: error.errorMessage,
      [ApplicationErrorDataKey.APP_ERROR_DESCRIPTION]: error.contextDescription,
      [ApplicationErrorDataKey.APP_ERROR_INTERNAL]: error.internalError,
      [ApplicationErrorDataKey.APP_ERROR_TYPE]: 'PLAYBACK_ERROR',
      [ApplicationErrorDataKey.APP_ERROR_SEVERITY]: ErrorSeverity.CRITICAL,
    };

    const isContentError = this.isMainContext(this.player);
    if (!isContentError) {
      this.onAdError(toErrorMessage(error));
    }
    Object.values(this.reporters).forEach((reporter: AnalyticsReporter) => {
      if (reporter.provider === Provider.GODAVARI) {
        if (!isContentError) {
          return;
        }
        reporter.reportMetadata(this.buildContentMetadata(reporter, this.content));
        reporter.reportEvent(PlaybackEvent.PLAYBACK_ERROR, {
          errorCode: error.hexErrorCode,
          errorMessage: error.errorMessage,
          exception: error.internalError,
        });
        return;
      }
      if (!this.player?.playbackStatistics) {
        return;
      }

      const { lastPlayedVariantBandwidth, playTime } = this.player.playbackStatistics;
      const currentPosition = playTime != null && playTime > 0 ? playTime : (this.player?.currentTime ?? 0);

      if (reporter.provider === Provider.NEWRELIC) {
        reporter.reportEvent(ApplicationEvent.ERROR, {
          ...this.buildNewRelicContentMetadata(this.content),
          ...playbackError,
          [PlaybackDataKey.CURRENT_POSITION]: Math.ceil(currentPosition),
          [PlaybackDataKey.LAST_PLAYED_VARIANT]: lastPlayedVariantBandwidth ?? undefined,
        });
        return;
      }
      reporter.reportEvent(PlaybackEvent.PLAYBACK_ERROR, {
        errorCode: error.hexErrorCode,
        errorMessage: `${error.hexErrorCode} (${error.internalError?.errorCode}) | ${error.errorMessage}`,
      });
    });
  };

  private readonly onAdError = (adError: any): void => {
    if (!this.isAdBreakStartReported) {
      this.onAdBreakStart();
    }

    // Normalize error message
    const isIMAAdError = typeof adError?.getMessage === 'function' && typeof adError?.getErrorCode === 'function';

    const errorMessage = isIMAAdError ? `${adError.getMessage()} (${adError.getErrorCode()})` : JSON.stringify(adError);

    // Log error
    this.loggers &&
      Object.values(this.loggers).forEach((logger: Logger) =>
        logger.log('[ANALYTICS] ad error', errorMessage, adError),
      );

    // Prepare structured error data
    const aderrorData = {
      message: errorMessage,
      code: isIMAAdError ? String(adError.getErrorCode()) : adError.hexErrorCode,
      internalError: isIMAAdError ? adError.getInnerError() : adError.internalError,
      description: isIMAAdError ? adError.getType?.() : adError.contextDescription,
      errorType: 'AD_ERROR',
      severity: ErrorSeverity.WARNING,
    };

    // Report ad error
    Object.values(this.reporters).forEach((reporter: AnalyticsReporter) => {
      if (reporter.provider === Provider.NEWRELIC) {
        reporter.reportEvent(ApplicationEvent.ERROR, {
          ...this.buildNewRelicContentMetadata(this.content),
          ...toApplicationErrorReportingData(aderrorData, reporter.provider),
        });
      } else {
        reporter.reportEvent(AdPlaybackEvent.AD_ERROR, {
          errorCode: aderrorData.code,
          errorMessage,
          exception: aderrorData.description,
          adBreak: this.resolveAdBreak(this.activeAdBreak),
        });
      }
    });

    if (!this.isAdBreakEndReported) {
      this.onAdBreakEnd();
    }
  };

  private readonly onStreamEnded = (): void => {
    Object.values(this.reporters).forEach((reporter: AnalyticsReporter) => {
      switch (reporter.provider) {
        case Provider.DATAZOOM:
          // No explicit stream-ended reporting for Datazoom (intentionally skipped)
          break;

        case Provider.CONVIVA:
          reporter.reportEvent(PlaybackEvent.PLAYBACK_END);
          break;

        case Provider.GODAVARI:
          reporter.reportMetadata(this.buildContentMetadata(reporter, this.content));
          reporter.reportEvent(PlaybackEvent.PLAYBACK_COMPLETE);
          break;

        case Provider.NEWRELIC:
          reporter.reportEvent(PlaybackEvent.PLAYBACK_COMPLETE, {
            ...this.buildNewRelicContentMetadata(this.content),
            [PlaybackDataKey.AUTO_PREVIEW]: this.content.isAutoPreview,
          });
          break;
      }
    });
  };

  /**
   * Builds playback metadata to be reported.
   * @private
   */
  private buildMetadata(): { [key: string]: any } {
    const audioVideoMetadata = this.getAudioVideoMetadata(this.player);

    return {
      [PlaybackDataKey.VIDEO_VARIANT]: audioVideoMetadata?.videoVariant,
      [PlaybackDataKey.AUDIO_VARIANT]: audioVideoMetadata?.audioVariant,
      [PlaybackDataKey.VIDEO_CODEC]: audioVideoMetadata?.videoCodec,
      [PlaybackDataKey.AUDIO_CODEC]: audioVideoMetadata?.audioCodec,
      [PlaybackDataKey.PLAYBACK_SOURCE]: this.playbackSource,
      [PlaybackDataKey.CONTENT_DURATION_MILLISECONDS]: Math.ceil((this.player?.duration ?? 0) * 1000),
      [PlaybackDataKey.CONTENT_PLAYHEAD_POSITION_MILLISECONDS]: Math.ceil((this.player?.currentTime ?? 0) * 1000),
    };
  }

  /**
   * Safely retrieves the playback URL from the raw player if available.
   * For Janus WebRTC players, constructs URL from session information.
   * For other players, retrieves the asset URI.
   * @private
   */
  private getPlaybackUrl(): string {
    try {
      const rawPlayer = this.player?.rawPlayer;

      // Handle Janus WebRTC player
      if (this.player?.internalPlayerName === 'janus-webrtc-player') {
        const session = rawPlayer?.session;
        if (session?.getServer && session?.getSessionId) {
          const server = session.getServer();
          const sessionId = session.getSessionId();
          return `${server}?sId=${sessionId}`;
        }
      }

      // Fall back to asset URI for other players
      return rawPlayer?.getAssetUri?.() ?? 'N/A';
    } catch {
      return 'N/A';
    }
  }

  /**
   * Safely retrieves the DRM scheme from the raw player if available.
   * @private
   */
  private getDrmScheme(): DrmScheme {
    try {
      const rawPlayer = this.player?.rawPlayer;
      const keySystem = rawPlayer?.keySystem?.();
      return mapKeySystemToDrmScheme(keySystem) ?? DrmScheme.NONE;
    } catch {
      return DrmScheme.NONE;
    }
  }

  private buildNewRelicContentMetadata(content: Content): { [key: string]: any } {
    const player = this.player;
    const playbackStatistics = player?.playbackStatistics;
    const activeTracks = player?.getActiveTracks() ?? [];
    const audioVideoMetadata = this.getAudioVideoMetadata(player);

    const toFiniteNumber = (value: unknown): number | undefined => {
      if (value == null) return undefined;
      const num = typeof value === 'number' ? value : Number(value);
      return Number.isFinite(num) ? num : undefined;
    };

    const textTrack = activeTracks.find(track => track.type === TrackType.TEXT);
    const audioTrack = activeTracks.find(track => track.type === TrackType.AUDIO);
    const isCloseCaptionsEnabled = activeTracks.some(track => track.type === 'text' && track.kind === 'caption');

    const loadLatencySec = toFiniteNumber(playbackStatistics?.loadLatency);
    const vstPlayerPrepareMs = loadLatencySec === undefined ? undefined : loadLatencySec * 1000;

    return {
      ...toNewRelicContentData(content),
      ...(this.player &&
        this.instanceOfAdComposedPlayer(this.player) && { [PlaybackDataKey.AD_SESSION_ID]: this.player.adSessionId }),
      [PlaybackDataKey.PLAYBACK_SESSION_ID]: this.playbackSessionId,
      [PlaybackDataKey.PROGRAM_PLAYBACK_SESSION_ID]: this.programPlaybackSessionId,
      [PlaybackDataKey.CC_ENABLED]: isCloseCaptionsEnabled,
      [PlaybackDataKey.PLAYER_NAME]: PLAYER_NAME,
      [PlaybackDataKey.CURRENT_BANDWIDTH_KB]: toFiniteNumber(playbackStatistics?.estimatedBandwidth),
      [PlaybackDataKey.VIDEO_RESOLUTION]: audioVideoMetadata?.resolution,
      [PlaybackDataKey.HIGHEST_VARIANT_BANDWIDTH_KB]: audioVideoMetadata?.highestVariantBandwidth,
      [PlaybackDataKey.LOWEST_VARIANT_BANDWIDTH_KB]: audioVideoMetadata?.lowestVariantBandwidth,
      [PlaybackDataKey.PLAYBACK_DURATION_SEC]: toFiniteNumber(playbackStatistics?.playTime),
      // PROGRAM_PLAYBACK_DURATION_SEC is reported only for live streams.
      // For VOD content, this value is undefined, so it is not reported.
      [PlaybackDataKey.PROGRAM_PLAYBACK_DURATION_SEC]: toFiniteNumber(playbackStatistics?.programWatchedDuration),
      [PlaybackDataKey.CONTENT_DURATION_SEC]: toFiniteNumber(this.content.duration),
      [PlaybackDataKey.PLAYBACK_URL]: this.getPlaybackUrl(),
      [PlaybackDataKey.DRM_NAME]: this.getDrmScheme(),
      [PlaybackDataKey.VST_LICENSE_MS]: toFiniteNumber(playbackStatistics?.licenseTime),
      [PlaybackDataKey.VST_PLAYER_PREPARE_MS]: vstPlayerPrepareMs,
      [PlaybackDataKey.VST_STREAMLOAD_MS]: toFiniteNumber(playbackStatistics?.ssaiStreamUrlFetchTimeInMS),
      [PlaybackDataKey.VST_PREROLL_MS]: toFiniteNumber(playbackStatistics?.preRollAdLoadTimeMs),
      [PlaybackDataKey.AUDIO_LANGUAGE]: audioTrack?.language,
      [PlaybackDataKey.SUBTITLE_LANGUAGE]: textTrack?.language,

      ...this.content.customMetadata,
    };
  }

  /**
   * Builds ad playback metadata to be reported.
   * @param adInfo
   * @private
   */
  private buildAdMetadata(adInfo: AdInfo | Ad, reporter?: string): { [key: string]: any } {
    const isAdInfo = this.instanceOfAdInfo(adInfo);
    const isNewRelic = reporter === Provider.NEWRELIC;

    const baseData = {
      [AdDataKey.AD_TITLE]: isAdInfo ? adInfo.vastProperties?.adTitle : this.activeAd?.vastProperties?.adTitle,
      [AdDataKey.AD_TYPE]: isAdInfo ? adInfo.adBreakInfo?.contentTimePosition : adInfo.adBreak?.position,
      [AdDataKey.AD_CREATIVE_ID]: isAdInfo
        ? adInfo.vastProperties?.creativeId
        : this.activeAd?.vastProperties?.creativeId,
    };

    const durationData = isAdInfo
      ? this.buildAdInfoDurationData(adInfo, isNewRelic)
      : this.buildAdObjectDurationData(adInfo, isNewRelic);

    return { ...baseData, ...durationData };
  }

  private buildAdInfoDurationData(adInfo: AdInfo, isNewRelic: boolean): { [key: string]: any } {
    if (isNewRelic) {
      return {
        [AdDataKey.AD_DURATION]: (adInfo.durationMs ?? 0) / 1000,
        [AdDataKey.AD_SKIP_OFFSET]:
          adInfo.skipOffsetMs && adInfo.skipOffsetMs > 0 ? adInfo.skipOffsetMs / 1000 : undefined,
      };
    }
    return {
      [AdDataKey.AD_ID]: adInfo.adID,
      [AdDataKey.AD_DURATION_MS]: adInfo.durationMs ?? 0,
      [AdDataKey.AD_SKIP_OFFSET_MS]: adInfo.skipOffsetMs ?? 0,
    };
  }

  private buildAdObjectDurationData(ad: Ad, isNewRelic: boolean): { [key: string]: any } {
    if (isNewRelic) {
      return {
        [AdDataKey.AD_DURATION]: ad.duration,
        [AdDataKey.AD_SKIP_OFFSET]: ad.skipOffset && ad.skipOffset > 0 ? ad.skipOffset : undefined,
      };
    }
    return {
      [AdDataKey.AD_ID]: ad.adId,
      [AdDataKey.AD_DURATION_MS]: Math.ceil((ad.duration ?? 0) * 1000),
      [AdDataKey.AD_SKIP_OFFSET_MS]: Math.ceil((ad.skipOffset ?? 0) * 1000),
      [PlaybackDataKey.CONTENT_DURATION_MILLISECONDS]: ad.contentDurationMs,
      [PlaybackDataKey.CONTENT_PLAYHEAD_POSITION_MILLISECONDS]: ad.contentPlayheadPositionMs,
    };
  }

  /**
   * Builds the audio and video metadata to be reported.
   * @param player the player instance
   * @private
   */

  private getAudioVideoMetadata(player?: any): { [key: string]: any } | undefined {
    if (!player) {
      return undefined;
    }

    const variantTracks = player?.rawPlayer?.getVariantTracks?.();
    if (!variantTracks || !Array.isArray(variantTracks)) {
      return undefined;
    }

    const activeVariantTrack = variantTracks.find((variant: any) => variant.active);
    const highestVariantTrack = variantTracks[variantTracks.length - 1];
    const lowestVariantTrack = variantTracks[0];
    if (!activeVariantTrack) {
      this.loggers?.[Provider.NEWRELIC]?.warn?.('[ANALYTICS][getAudioVideoMetadata] No active variant track found');
      return undefined;
    }

    const id = activeVariantTrack.id;
    const videoId = activeVariantTrack.videoId;
    const width = activeVariantTrack.width;
    const height = activeVariantTrack.height;
    const bandwidth = Math.trunc(activeVariantTrack.bandwidth / 1000);
    const audioId = activeVariantTrack.audioId;
    const audioSamplingRate = Math.trunc(activeVariantTrack.audioSamplingRate / 1000);
    const [, containerFormat] = activeVariantTrack.mimeType?.split('/') ?? [];

    return {
      videoVariant: [id, videoId, width, height, bandwidth].filter(Boolean).join('_'),
      audioVariant: [id, audioId, audioSamplingRate].filter(Boolean).join('_'),
      audioCodec: activeVariantTrack.audioCodec,
      videoCodec: activeVariantTrack.videoCodec,
      resolution: `${width}x${height}`,
      audioLanguage: activeVariantTrack.language,
      highestVariantBandwidth: highestVariantTrack.bandwidth,
      lowestVariantBandwidth: lowestVariantTrack.bandwidth,
      containerFormat,
    };
  }

  private instanceOfAdInfo(content: any): content is AdInfo {
    return content && typeof content === 'object' && 'adStartTimeOffsetMs' in content;
  }

  /**
   * This guard will check whether an instance of Player implements
   * the AdComposedPlayer interface or not.
   * @param player the Player instance to check
   */
  private instanceOfAdComposedPlayer(player: Player): player is AdComposedPlayer {
    return (player as AdComposedPlayer).playerContext !== undefined;
  }

  private isMainContext(player: Player | undefined): boolean {
    if (player && this.instanceOfAdComposedPlayer(player)) {
      return player.playerContext === PlayerContext.MAIN;
    }
    return true;
  }

  private startTimeout(time: number): void {
    if (this.timeoutStarted) {
      this.timeoutController.clear();
      this.timeoutStarted = false;
    }

    this.timeoutController.set(() => {
      this.stop();
      this.timeoutController.clear();
      this.timeoutStarted = false;

      this.reporters?.[Provider.CONVIVA].reset();

      this.loggers?.[Provider.CONVIVA].log(
        `[ANALYTICS][COST_OPTIMIZATION] stopped reporting after ${time / 1000} seconds`,
      );
    }, time);

    this.timeoutStarted = true;

    this.loggers?.[Provider.CONVIVA].log(
      `[ANALYTICS][COST_OPTIMIZATION] will stop reporting in ${time / 1000} seconds`,
    );
  }

  private reportEvent(event: string, data?: { [key: string]: any }, context?: any): void {
    if (!this.reporters) {
      return;
    }
    Object.values(this.reporters).forEach((reporter: AnalyticsReporter) => {
      reporter.reportEvent(event, data, context);
    });
  }

  private buildNPAWVideoMetadata(): { [key: string]: any } {
    const audioVideoMetadata = this.getAudioVideoMetadata(this.player);
    return {
      ...(this.player &&
        this.instanceOfAdComposedPlayer(this.player) && {
        'content.metadata': {
          [PlaybackDataKey.AD_SESSION_ID]: this.player.adSessionId,
        },
      }),
      'content.encoding.videoCodec': audioVideoMetadata?.videoCodec,
      'content.encoding.audioCodec': audioVideoMetadata?.audioCodec,
      'content.duration': this.player?.duration ? Math.ceil(this.player.duration * 1000) : 0,
      'content.title': this.content?.name,
      'content.program': this.content?.programName,
      'content.isLive': this.content?.isLive === StreamType.LIVE,
      'content.package': this.content?.packageName,
      'content.contractedResolution': this.content?.contractedResolution,
      'content.saga': this.content?.saga,
      'content.subtitles': this.content?.subtitles,
      'content.encoding.codecSettings': this.content?.codecSettings,
      'content.encoding.codecProfile': this.content?.codecProfile,
      'content.drm': this.getDrmScheme(),
      'content.encoding.containerFormat': audioVideoMetadata?.containerFormat || '',
    };
  }

  private buildContentMetadata(
    reporter: AnalyticsReporter,
    content: Content,
    observation?: { statistics: PlaybackStatistics | undefined },
  ): { [key: string]: any } {
    switch (reporter.provider) {
      case Provider.GODAVARI:
        return this.buildGodavariContentMetadata(content, observation);
      case Provider.CONVIVA: {
        return toConvivaReportingData(content);
      }
      case Provider.DATAZOOM: {
        return toDatazoomReportingData(content);
      }
      case Provider.NPAW: {
        return toNPAWReportingData(content);
      }

      case Provider.NEWRELIC: {
        return this.buildNewRelicContentMetadata(content);
      }
      default: {
        return {};
      }
    }
  }

  private buildGodavariContentMetadata(
    content: Content,
    observation?: { statistics: PlaybackStatistics | undefined },
  ): { [key: string]: any } {
    const player = this.player;
    const stats = observation ? observation.statistics : player?.playbackStatistics;
    const variantMetadata = this.getAudioVideoMetadata(player);
    if (variantMetadata?.resolution && !variantMetadata.resolution.includes('undefined')) {
      this.lastKnownResolution = variantMetadata.resolution;
    }
    // vs-vr is mandatory on every beacon, but no variant is active before startup or during ads.
    const audioVideoMetadata = {
      ...variantMetadata,
      resolution: this.lastKnownResolution ?? `${window.screen?.width ?? 0}x${window.screen?.height ?? 0}`,
    };
    const activeTracks = player?.getActiveTracks() ?? [];
    const playbackUrl = this.getPlaybackUrl();
    const streamUrl = playbackUrl && playbackUrl !== 'N/A' ? playbackUrl : content.streamUrl || '';
    const resourceTimings = streamUrl && performance.getEntriesByName ? performance.getEntriesByName(streamUrl) : [];
    const drmScheme = this.getDrmScheme();
    const streamingHost = this.getGodavariStreamingHost(streamUrl);
    this.updateGodavariWatchTime(stats);
    this.godavariObserver.captureStartingBitrate(stats?.variantBitrate);

    return toGodavariPlayerMetadata({
      content,
      player: {
        sdkVersion: version,
        currentTime: player?.currentTime,
        duration: player?.duration,
        isLive: player?.isLive,
        playbackRate: player?.playbackRate,
      },
      statistics: stats,
      audioVideoMetadata,
      activeTracks,
      streamUrl,
      streamingHost,
      resourceTimings,
      drmScheme,
      contentId: content.numericContentId ?? toGodavariContentId(content.externalContentId || content.id, this.loggers?.[Provider.GODAVARI]),
      playbackSessionId: this.playbackSessionId,
      playbackSessionStartTimeMs: this.playbackSessionStartTimeMs,
      startingBitrate: this.godavariObserver.startingBitrate,
      normalizedWatchTime: this.godavariObserver.normalizedWatchTime,
    });
  }

  private getGodavariStreamingHost(streamUrl: string): string | undefined {
    if (!streamUrl) return undefined;
    try {
      return new URL(streamUrl, window.location.href).hostname;
    } catch (error) {
      this.loggers?.[Provider.GODAVARI]?.warn(
        'Godavari cannot extract the streaming host from the manifest URL.',
        error,
      );
      return undefined;
    }
  }

  private updateGodavariWatchTime(stats: PlaybackStatistics | undefined): void {
    this.godavariObserver.updateWatchTime(
      Number(stats?.playTime),
      !this.isAdBreakStartReported && this.isMainContext(this.player),
    );
  }

  private godavariProgressSnapshot(observation?: {
    statistics: PlaybackStatistics | undefined;
  }): GodavariProgressSnapshot {
    const stats = observation ? observation.statistics : this.player?.playbackStatistics;
    return {
      position: this.player?.currentTime ?? 0,
      playTime: stats?.playTime ?? 0,
      isBuffering: this.player?.isBuffering,
    };
  }

  private calculatePlaybackStartupTimeIfNeeded(): void {
    if (this.playbackStartedTimeSec !== undefined || this.playbackLoadStartTime === 0) {
      return;
    }

    this.playbackStartedTimeSec = Math.ceil((Date.now() - this.playbackLoadStartTime) / 1000);
    this.playbackLoadStartTime = 0;
  }
}
